import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiClient, formatApiError } from "@/lib/api";
import { formatGNF, imgUrl } from "@/utils/format";
import { toast } from "sonner";
import { ChefHat, Loader2, PackageX, PackageCheck, Search, Plus, Pencil, CupSoda } from "lucide-react";
import DishFormModal from "@/components/DishFormModal";

/**
 * Menu manager for the restaurant dashboard.
 * - Lists all dishes of the current restaurant.
 * - 1-click toggle "Épuisé aujourd'hui" (sold_out_today). Auto-resets next day server-side.
 */
export default function RestaurantMenuPanel() {
  const { t } = useTranslation();
  const [dishes, setDishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({}); // dish_id -> bool
  const [query, setQuery] = useState("");
  const [modal, setModal] = useState({ open: false, dish: null, category: null });

  const refresh = async () => {
    try {
      const { data } = await apiClient.get("/my/restaurant");
      setDishes(data?.dishes || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const toggle = async (dish) => {
    const next = !dish.sold_out_today;
    setBusy((b) => ({ ...b, [dish.dish_id]: true }));
    try {
      await apiClient.patch(`/my/restaurant/dishes/${dish.dish_id}/sold-out`, { sold_out: next });
      setDishes((ds) => ds.map((d) => d.dish_id === dish.dish_id ? { ...d, sold_out_today: next } : d));
      toast.success(next ? `${dish.name} — ${t("rdash.toastSoldOut")}` : `${dish.name} — ${t("rdash.toastBack")}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy((b) => ({ ...b, [dish.dish_id]: false }));
    }
  };

  if (loading) return <div className="text-mute py-8 text-center">Chargement du menu…</div>;

  const openAdd = () => setModal({ open: true, dish: null, category: null });
  const openAddCategory = (category) => setModal({ open: true, dish: null, category });
  const openEdit = (d) => setModal({ open: true, dish: d, category: null });
  const closeModal = () => setModal({ open: false, dish: null, category: null });
  const onSaved = (d) => {
    setDishes((ds) => {
      const idx = ds.findIndex((x) => x.dish_id === d.dish_id);
      if (idx >= 0) { const cp = [...ds]; cp[idx] = { ...ds[idx], ...d }; return cp; }
      return [d, ...ds];
    });
    closeModal();
  };
  const onDeleted = (dishId) => {
    setDishes((ds) => ds.filter((x) => x.dish_id !== dishId));
    closeModal();
  };

  if (!dishes.length) return (
    <>
      <div className="bg-white border border-sand rounded-2xl p-10 text-center" data-testid="menu-empty">
        <ChefHat className="w-10 h-10 mx-auto mb-2 opacity-40" />
        <div className="text-mute mb-4">{t("rdash.menuEmpty")}</div>
        <button
          onClick={openAdd}
          data-testid="menu-add-first-dish"
          className="inline-flex items-center gap-2 rounded-full bg-brand-green hover:bg-[#007A4A] text-white px-5 py-2.5 text-sm font-semibold"
        >
          <Plus className="w-4 h-4" /> {t("rdash.addFirst")}
        </button>
      </div>
      <DishFormModal open={modal.open} dish={modal.dish} initialCategory={modal.category} onClose={closeModal} onSaved={onSaved} onDeleted={onDeleted} />
    </>
  );

  const filtered = dishes.filter((d) => !query || d.name.toLowerCase().includes(query.toLowerCase()));
  const soldOutCount = dishes.filter((d) => d.sold_out_today).length;
  // Nudge "Boissons manquantes" : le restaurant n'a aucune boisson NI dessert
  // disponible → il ne profite pas des suggestions au paiement (panier moyen).
  const hasDrinksOrDesserts = dishes.some(
    (d) => ["boissons", "desserts"].includes(d.category) && d.available !== false
  );

  return (
    <div className="space-y-4" data-testid="restaurant-menu-panel">
      {!hasDrinksOrDesserts && (
        <div
          data-testid="missing-drinks-nudge"
          className="bg-gradient-to-r from-brand-green/10 to-brand-red/10 border border-brand-green/30 rounded-2xl p-4 flex items-start gap-3 flex-wrap sm:flex-nowrap"
        >
          <div className="shrink-0 mt-0.5 rounded-full bg-brand-green/15 p-2">
            <CupSoda className="w-5 h-5 text-brand-green" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-semibold text-sm">{t("rdash.nudgeTitle")}</div>
            <p className="text-xs text-mute mt-0.5">
              {t("rdash.nudgeText")}
            </p>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <button
              onClick={() => openAddCategory("boissons")}
              data-testid="nudge-add-drink"
              className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 rounded-full bg-brand-green hover:bg-[#007A4A] text-white px-4 py-2 text-xs font-semibold"
            >
              <Plus className="w-3.5 h-3.5" /> {t("rdash.nudgeDrink")}
            </button>
            <button
              onClick={() => openAddCategory("desserts")}
              data-testid="nudge-add-dessert"
              className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 rounded-full bg-white border border-brand-green/40 text-brand-green hover:bg-brand-green/5 px-4 py-2 text-xs font-semibold"
            >
              <Plus className="w-3.5 h-3.5" /> {t("rdash.nudgeDessert")}
            </button>
          </div>
        </div>
      )}
      <div className="bg-white border border-sand rounded-2xl p-4 flex items-center gap-3 flex-wrap">
        <div className="text-xs uppercase tracking-widest text-mute">
          <b className="text-ink text-sm normal-case">{dishes.length}</b> {t("rdash.platsWord")} · <span className="text-brand-red font-semibold">{soldOutCount}</span> {t("rdash.soldOutSuffix")}
        </div>
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-mute" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("rdash.searchDish")}
            data-testid="menu-search"
            className="w-full pl-9 pr-3 py-2 rounded-xl bg-elevated text-sm outline-none focus:ring-2 focus:ring-brand-red/30"
          />
        </div>
        <button
          onClick={openAdd}
          data-testid="menu-add-dish"
          className="inline-flex items-center gap-1.5 rounded-full bg-brand-green hover:bg-[#007A4A] text-white px-4 py-2 text-sm font-semibold"
        >
          <Plus className="w-4 h-4" /> {t("rdash.addDish")}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map((d) => (
          <div
            key={d.dish_id}
            data-testid={`menu-dish-${d.dish_id}`}
            className={`bg-white border rounded-2xl overflow-hidden ${d.sold_out_today ? "border-brand-red/40" : "border-sand"}`}
          >
            <button
              type="button"
              onClick={() => openEdit(d)}
              data-testid={`menu-dish-edit-${d.dish_id}`}
              className="relative aspect-[16/9] w-full bg-elevated block group"
              aria-label={`Modifier ${d.name}`}
            >
              {d.image ? (
                <img src={imgUrl(d.image)} alt={d.name} className={`w-full h-full object-cover ${d.sold_out_today ? "grayscale opacity-70" : ""}`} loading="lazy" />
              ) : (
                <div className="w-full h-full grid place-items-center text-mute text-xs">Pas de photo — cliquez pour ajouter</div>
              )}
              {d.sold_out_today && (
                <div className="absolute top-2 left-2 bg-brand-red text-white text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full">
                  {t("rdash.soldOutBadge")}
                </div>
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors grid place-items-center opacity-0 group-hover:opacity-100">
                <span className="rounded-full bg-white text-ink text-xs font-semibold px-3 py-1.5 inline-flex items-center gap-1.5">
                  <Pencil className="w-3.5 h-3.5" /> {t("rdash.editBtn")}
                </span>
              </div>
            </button>
            <div className="p-3">
              <div className="font-semibold text-sm line-clamp-1">{d.name}</div>
              <div className="text-xs text-mute">{formatGNF(d.price)}</div>
              <button
                onClick={() => toggle(d)}
                disabled={busy[d.dish_id]}
                data-testid={`toggle-soldout-${d.dish_id}`}
                className={`mt-3 w-full rounded-full py-2 text-xs font-semibold inline-flex items-center justify-center gap-1.5 transition-colors ${d.sold_out_today ? "bg-brand-green text-white hover:bg-[#007A4A]" : "bg-elevated text-ink hover:bg-brand-red hover:text-white"}`}
              >
                {busy[d.dish_id] ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : d.sold_out_today ? (
                  <>
                    <PackageCheck className="w-3.5 h-3.5" /> {t("rdash.makeAvailable")}
                  </>
                ) : (
                  <>
                    <PackageX className="w-3.5 h-3.5" /> {t("rdash.markSoldOut")}
                  </>
                )}
              </button>
            </div>
          </div>
        ))}
      </div>

      <DishFormModal open={modal.open} dish={modal.dish} initialCategory={modal.category} onClose={closeModal} onSaved={onSaved} onDeleted={onDeleted} />
    </div>
  );
}
